import { mixedVolume, subscribeAudioSettings } from "./audioSettings";
import {
  LEVEL01_BGM_URL,
  LEVEL01_BGM_VOLUME,
  LEVEL01_BOSS_BGM_URL,
  shouldLoopLevel01BossTheme,
  shouldLoopLevel01Theme
} from "./level01Bgm";
import {
  LEVEL02_BGM_URL,
  LEVEL02_BGM_VOLUME,
  LEVEL02_BOSS_BGM_URL,
  shouldLoopLevel02BossTheme,
  shouldLoopLevel02Theme
} from "./level02Bgm";
import {
  LEVEL03_BGM_URL,
  LEVEL03_BGM_VOLUME,
  LEVEL03_BOSS_BGM_URL,
  LEVEL03_FINAL_FORM_BGM_URL,
  shouldLoopLevel03BossTheme,
  shouldLoopLevel03FinalFormTheme,
  shouldLoopLevel03Theme
} from "./level03Bgm";
import {
  LEVEL04_BGM_URL,
  LEVEL04_BGM_VOLUME,
  LEVEL04_BOSS_BGM_URL,
  shouldLoopLevel04BossTheme,
  shouldLoopLevel04Theme
} from "./level04Bgm";

export type GameBgmId =
  | "level01"
  | "level01-boss"
  | "level02"
  | "level02-boss"
  | "level03"
  | "level03-boss"
  | "level03-final-form"
  | "level04"
  | "level04-boss";

export const GAME_BGM_TRACKS: Record<GameBgmId, { url: string; volume: number }> = {
  level01: { url: LEVEL01_BGM_URL, volume: LEVEL01_BGM_VOLUME },
  "level01-boss": { url: LEVEL01_BOSS_BGM_URL, volume: LEVEL01_BGM_VOLUME },
  level02: { url: LEVEL02_BGM_URL, volume: LEVEL02_BGM_VOLUME },
  "level02-boss": { url: LEVEL02_BOSS_BGM_URL, volume: LEVEL02_BGM_VOLUME },
  level03: { url: LEVEL03_BGM_URL, volume: LEVEL03_BGM_VOLUME },
  "level03-boss": { url: LEVEL03_BOSS_BGM_URL, volume: LEVEL03_BGM_VOLUME },
  "level03-final-form": { url: LEVEL03_FINAL_FORM_BGM_URL, volume: LEVEL03_BGM_VOLUME },
  level04: { url: LEVEL04_BGM_URL, volume: LEVEL04_BGM_VOLUME },
  "level04-boss": { url: LEVEL04_BOSS_BGM_URL, volume: LEVEL04_BGM_VOLUME }
};

const STOP_GAME_BGM = "__breakroomStopGameBgm";
const GAME_BGM_ATTR = "data-game-bgm";

type GameBgmHost = typeof globalThis & {
  [STOP_GAME_BGM]?: () => void;
};

let current: HTMLAudioElement | undefined;
let currentId: GameBgmId | undefined;
let unlockBound = false;
let settingsUnsub: (() => void) | undefined;
let crossfadeFrame: number | undefined;
let fadingOut: HTMLAudioElement | undefined;
let crossfadeProgress = 1;

export function resolveGameBgmId(input: {
  levelIndex: number;
  activeWaveId: string | null;
  practiceBossStart: boolean;
  silencedForBoss: boolean;
  darioSecondForm?: boolean;
}): GameBgmId | null {
  if (shouldLoopLevel03FinalFormTheme(input)) return "level03-final-form";
  if (shouldLoopLevel01BossTheme(input)) return "level01-boss";
  if (shouldLoopLevel02BossTheme(input)) return "level02-boss";
  if (shouldLoopLevel03BossTheme(input)) return "level03-boss";
  if (shouldLoopLevel04BossTheme(input)) return "level04-boss";
  if (shouldLoopLevel01Theme(input)) return "level01";
  if (shouldLoopLevel02Theme(input)) return "level02";
  if (shouldLoopLevel03Theme(input)) return "level03";
  if (shouldLoopLevel04Theme(input)) return "level04";
  return null;
}

export function isMenuElementVisible(menu = document.getElementById("menu")): boolean {
  return Boolean(menu && !menu.hidden);
}

export function pageAllowsGameTheme(
  pageVisible = !document.hidden,
  pageFocused = document.hasFocus(),
  menuVisible = isMenuElementVisible()
): boolean {
  return pageVisible && pageFocused && !menuVisible;
}

function targetVolume(id: GameBgmId): number {
  return mixedVolume("music", GAME_BGM_TRACKS[id].volume);
}

function haltElement(element: HTMLAudioElement): void {
  element.pause();
  element.currentTime = 0;
  element.removeAttribute("src");
  element.load();
  element.remove();
}

function sweepLeftovers(...keep: Array<HTMLAudioElement | undefined>): void {
  for (const leftover of document.querySelectorAll<HTMLAudioElement>(`audio[${GAME_BGM_ATTR}]`)) {
    if (keep.includes(leftover)) continue;
    haltElement(leftover);
  }
}

function applyVolume(): void {
  if (!current || !currentId) return;
  current.volume = targetVolume(currentId) * crossfadeProgress;
}

function cancelCrossfade(): void {
  if (crossfadeFrame !== undefined) cancelAnimationFrame(crossfadeFrame);
  crossfadeFrame = undefined;
  crossfadeProgress = 1;
  if (fadingOut && fadingOut !== current) haltElement(fadingOut);
  fadingOut = undefined;
}

function bindUnlock(): void {
  if (unlockBound) return;
  unlockBound = true;
  const unlock = (): void => {
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    unlockBound = false;
    if (currentId) playGameBgm(currentId);
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
}

function ensureSettingsHook(): void {
  settingsUnsub ??= subscribeAudioSettings(applyVolume);
}

export function playGameBgm(id: GameBgmId): void {
  if (isMenuElementVisible()) {
    stopGameBgm();
    return;
  }
  ensureSettingsHook();
  for (const leftover of document.querySelectorAll<HTMLAudioElement>("audio[data-menu-bgm]")) {
    leftover.pause();
  }

  if (currentId === id && current && (crossfadeFrame !== undefined || !current.paused)) {
    applyVolume();
    return;
  }

  cancelCrossfade();
  sweepLeftovers(current);
  const track = GAME_BGM_TRACKS[id];
  const audio = current?.isConnected ? current : new Audio();
  if (current && current !== audio) haltElement(current);

  current = audio;
  currentId = id;
  audio.loop = true;
  audio.preload = "auto";
  audio.hidden = true;
  audio.autoplay = false;
  audio.dataset.gameBgm = id;
  if (!audio.isConnected) document.body.append(audio);

  const nextSrc = new URL(track.url, document.baseURI).href;
  if (audio.src !== nextSrc) {
    audio.src = track.url;
  }

  applyVolume();
  if (targetVolume(id) <= 0) {
    audio.pause();
    return;
  }

  const play = audio.play();
  if (play) void play.catch(() => bindUnlock());
}

export function crossfadeGameBgm(id: GameBgmId, durationMs = 1_200): void {
  if (isMenuElementVisible()) {
    stopGameBgm();
    return;
  }
  ensureSettingsHook();
  if (currentId === id) {
    playGameBgm(id);
    return;
  }

  cancelCrossfade();
  const outgoing = current;
  const outgoingStartVolume = outgoing?.volume ?? 0;
  sweepLeftovers(outgoing);

  const track = GAME_BGM_TRACKS[id];
  const incoming = new Audio();
  current = incoming;
  currentId = id;
  fadingOut = outgoing;
  crossfadeProgress = 0;
  incoming.loop = true;
  incoming.preload = "auto";
  incoming.hidden = true;
  incoming.autoplay = false;
  incoming.dataset.gameBgm = id;
  incoming.src = track.url;
  incoming.volume = 0;
  document.body.append(incoming);

  const play = incoming.play();
  if (play) void play.catch(() => bindUnlock());

  const startedAt = performance.now();
  const fadeDuration = Math.max(1, durationMs);
  const animate = (now: number): void => {
    const progress = Math.min(1, (now - startedAt) / fadeDuration);
    crossfadeProgress = progress;
    incoming.volume = targetVolume(id) * progress;
    if (outgoing) outgoing.volume = outgoingStartVolume * (1 - progress);
    if (progress < 1) {
      crossfadeFrame = requestAnimationFrame(animate);
      return;
    }
    crossfadeFrame = undefined;
    crossfadeProgress = 1;
    if (outgoing) haltElement(outgoing);
    fadingOut = undefined;
    applyVolume();
  };
  crossfadeFrame = requestAnimationFrame(animate);
}

export function stopGameBgm(): void {
  const sound = current;
  cancelCrossfade();
  current = undefined;
  currentId = undefined;
  if (sound) haltElement(sound);
  sweepLeftovers();
}

export function destroyGameBgm(): void {
  const stopper = (globalThis as GameBgmHost)[STOP_GAME_BGM];
  if (stopper && stopper !== destroyGameBgm) {
    stopper();
    return;
  }
  settingsUnsub?.();
  settingsUnsub = undefined;
  unlockBound = false;
  stopGameBgm();
}

(globalThis as GameBgmHost)[STOP_GAME_BGM] = destroyGameBgm;

const hot = (import.meta as ImportMeta & { hot?: { dispose: (callback: () => void) => void } }).hot;
hot?.dispose(destroyGameBgm);
